import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useArtistStore } from "../../stores/artistStore.js";
import { useBandStore } from "../../stores/bandStore.js";
import { SLOT_ROLES, type SlotRole } from "../../infra/api.js";
import { Button } from "../../ui/Button.js";
import { LpcAvatar } from "./LpcAvatar.js";
import { AvatarFrame } from "./AvatarFrame.js";

const ROLE_LABELS: Record<SlotRole, string> = {
  vocal: "Vocal",
  guitar: "Guitarra",
  bass: "Baixo",
  drums: "Bateria",
  keys: "Teclado",
};

export function BandEdit(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const bands = useBandStore((s) => s.bands);
  const artists = useArtistStore((s) => s.artists);
  const band = bands.find((b) => b.id === id);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void useArtistStore.getState().fetch();
    void useBandStore.getState().fetch();
  }, []);

  useEffect(() => {
    if (band) setName(band.name);
  }, [band]);

  if (!band || !id)
    return (
      <p className="font-mono text-xs uppercase tracking-widest2 text-ink-muted">
        Banda não encontrada.
      </p>
    );

  async function onRename(): Promise<void> {
    if (!band || !id) return;
    setSaving(true);
    try {
      await useBandStore.getState().rename(id, name.trim());
    } finally {
      setSaving(false);
    }
  }

  async function onSetSlot(role: SlotRole, artistId: string): Promise<void> {
    if (!id) return;
    if (artistId === "") {
      await useBandStore.getState().removeSlot(id, role);
    } else {
      await useBandStore.getState().setSlot(id, role, artistId);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <header className="reveal flex items-end justify-between border-b border-border pb-6">
        <div>
          <p className="catalog-num mb-2">FICHA / EDIÇÃO</p>
          <h1
            className="font-display text-[52px] leading-[0.9] tracking-tight text-paper"
            style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
          >
            Editar <span className="italic text-vinyl">{band.name}</span>
          </h1>
        </div>
        <Button variant="ghost" onClick={() => nav(`/bands/${id}`)}>
          ← voltar
        </Button>
      </header>

      <div className="reveal-1 border border-border bg-tape p-5">
        <p className="catalog-num mb-2">NOME</p>
        <div className="flex gap-3">
          <input
            className="flex-1 border-0 border-b border-border bg-transparent px-0 py-2 font-display text-2xl text-paper outline-none focus:border-paper"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            variant="secondary"
            onClick={() => void onRename()}
            disabled={saving || name === band.name || !name.trim()}
          >
            Renomear
          </Button>
        </div>
      </div>

      <div className="reveal-2 border border-border bg-tape">
        <header className="border-b border-border px-5 py-3">
          <p className="catalog-num">ELENCO</p>
        </header>
        <div className="divide-y divide-border">
          {SLOT_ROLES.map((role) => {
            const slot = band.slots.find((s) => s.slot_role === role);
            return (
              <div key={role} className="grid grid-cols-12 items-center gap-3 px-5 py-3">
                <div className="col-span-3">
                  <p
                    className="font-display text-xl text-paper"
                    style={{ fontVariationSettings: '"opsz" 36, "wght" 500' }}
                  >
                    {ROLE_LABELS[role]}
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
                    .{role}
                  </p>
                </div>
                <div className="col-span-7">
                  <select
                    className="w-full border border-border bg-asphalt px-3 py-2 font-mono text-sm text-paper focus:border-paper focus:outline-none"
                    value={slot?.artist.id ?? ""}
                    onChange={(e) => void onSetSlot(role, e.target.value)}
                  >
                    <option value="">— vago —</option>
                    {artists.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 flex justify-end">
                  {slot ? (
                    <AvatarFrame size={48} tone="warm">
                      <LpcAvatar
                        layers={slot.artist.lpc_layers}
                        size={48}
                        label={slot.artist.name}
                        instrument={role}
                      />
                    </AvatarFrame>
                  ) : (
                    <div className="h-12 w-12 border border-dashed border-border" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
